// src/pages/LoginPage.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FlexCenter } from '../styles/commonStyles';
import { Button } from '../components/Button';
import { theme } from '../styles/theme';


const LoginWrapper = styled(FlexCenter)`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
  width:100%;
  margin:0;
  padding:0;

  // Responsive padding for the wrapper
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0 ${({ theme }) => theme.spacing.medium}; // Smaller padding on tablet
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 ${({ theme }) => theme.spacing.small}; // Even smaller padding on mobile
  }
`;

const LoginForm = styled.form`
  background: white;
  padding: ${({ theme }) => theme.spacing.medium};
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  display:flex;
  flex-direction: column;
  align-items: center;

  // Responsive adjustments for the form
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 350px; // Slightly smaller form width on tablet
    padding: ${({ theme }) => theme.spacing.small}; // Reduce padding
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: 300px; // Further reduce form width on mobile
    padding: ${({ theme }) => theme.spacing.small};
  }
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.title.fontSize};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  border: 1px solid ${({ theme }) => theme.colors.muted};
  border-radius: 5px;
  font-size: 1rem;
`;

const ErrorMessage = styled.p`
  color: red;
  font-size: 0.9rem;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`;

const BackToHomeLink = styled(Link)`
  margin-top: ${({ theme }) => theme.spacing.medium}; // Space above the link
  font-size: 0.9rem; 
  color: ${({ theme }) => theme.colors.primary}; 
  border: 1px solid ${({ theme }) => theme.colors.primary};
  padding: 5px 10px;
  border-radius:10px;
  text-decoration: none; 
  position: relative; // For the animated underline effect
  transition: color 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary}; // Olive gray on hover
    color: white;
    
  }

  // Responsive font size
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 0.85rem;
    margin-top: ${({ theme }) => theme.spacing.small};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.8rem;
    margin-top: ${({ theme }) => theme.spacing.small};
  }
`;

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const loggedInUser = await login(username, password);
      if (loggedInUser.role === 'admin') {
      navigate('/admin');
    } else if (loggedInUser.role === 'manager') {
      navigate('/manager');
    } else {
      navigate('/employee'); // Redirect employees to landing page for now
    }
  }   catch (err) {
      setError('Invalid username or password');
    }
  };

  return (
    <LoginWrapper>      
        <LoginForm onSubmit={handleSubmit}>
          <Title>Login</Title>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Button as="button" type="submit">
            Log In    
          </Button>
          <BackToHomeLink to="/">Back to Home</BackToHomeLink>
        </LoginForm>      
    </LoginWrapper>
  );
};

export default LoginPage;